/**
 * 순찰 QR 찍기 페이지 — 2026-09-23 (시제품) → 2026-09-24 순찰 태블릿 + 좌석 점검표 (대표 결정)
 *
 * 두 가지로 열린다.
 *  ① 순찰 기기로 등록된 태블릿 → 순찰자 이름 + 4자리 번호 → QR 을 찍으면 그 구역 좌석 점검표.
 *     원장 아이디를 태블릿에 로그인해 두지 않는다(매출·연락처·설정이 다 열리기 때문).
 *  ② 등록 안 된 기기 → 종전처럼 매장 계정 로그인 상태에서 '순찰 완료 기록'만.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import { authedFetch } from '../infrastructure/api/authedFetch';

export const PATROL_DEVICE_KEY = 'patrol_device_token';
const SESSION_KEY = 'patrol_session';

interface Person { id: string; name: string; locked: boolean }
interface Seat {
  seat_number: number; seat_id: string; student_id: string | null; student_name: string | null;
  state: 'present' | 'outing' | 'exited' | 'not_checked_in' | null; outing_type: string | null;
  // 배치도 위치 (2026-10-02) — 출석부 배치도와 같은 좌표
  x: number | null; y: number | null; w: number; h: number; rotation: number; room_id: string | null; room_name: string | null;
}
interface Item { id: string; name: string; emoji: string; points: number; point_type: string; notify_parent: boolean }
interface ScanResult {
  scan_id: string; zone_name: string; staff_name: string; scanned_at: string;
  round: string | null; round_status: string | null; round_left: string[]; seats: Seat[]; items: Item[];
}
// 🔴 표시할 수 있는 것은 [벌점 관리] → 설정의 '순찰 항목' 하나뿐이다 (2026-10-01 대표 지시).
//    QR 전용 항목을 따로 만들면 출석부 순찰 버튼과 갈라져 관리가 두 벌이 된다.
type Mark = { kind: 'item'; item_id: string };

const STATE_LABEL: Record<string, { t: string; bg: string; fg: string }> = {
  present: { t: '입실 중', bg: '#dcfce7', fg: '#166534' },
  outing: { t: '외출 중', bg: '#fef3c7', fg: '#92400e' },
  exited: { t: '퇴실', bg: '#f3f4f6', fg: '#6b7280' },
  not_checked_in: { t: '오늘 안 옴', bg: '#f3f4f6', fg: '#6b7280' },
};

// 순찰 기기 요청 시간 제한 — 넘으면 실패로 보고 화면에 알린다 (2026-10-16)
const REQUEST_TIMEOUT_MS = 20_000;

// ⭐ 2026-10-17 우장산 (대표 지시) — QR 만 찍고 '점검 완료'를 안 누른 채 다음 구역으로 가면 그 칸이
//   '점검 안 함'으로 남는다. 이 기기에서 찍었는데 아직 완료 안 한 기록을 기억해 두고, 다음 QR 을 찍을 때 알린다.
//   🔴 앞 구역 QR 을 3분 넘게 지나 다시 찍어도 순찰표 칸은 고쳐지지 않는다(처음 기록을 쓴다, 서버 _pick_scan).
//      그래서 '다시 찍으세요'가 아니라 그 기록을 여기서 바로 완료한다. 서버는 QR 후 30분까지만 받는다.
const PENDING_KEY = 'patrol_pending_scans';
const PENDING_KEEP_MS = 2 * 60 * 60 * 1000;   // 이보다 오래된 건 잊는다
const COMPLETE_LIMIT_MS = 30 * 60 * 1000;      // 서버 device_complete 의 30분 제한과 같게
const DUP_SCAN_MS = 180 * 1000;                // 서버 DUP_SCAN_SECONDS 와 같게 — 같은 구역 재스캔은 한 방문
type PendingScan = { scan_id: string; zone_name: string; scanned_at: string; staff_name?: string };
const loadPending = (): PendingScan[] => {
  try {
    const list = JSON.parse(localStorage.getItem(PENDING_KEY) || '[]');
    if (!Array.isArray(list)) return [];
    return list.filter((p: PendingScan) => p && p.scan_id && Date.now() - new Date(p.scanned_at).getTime() < PENDING_KEEP_MS);
  } catch { return []; }
};
const savePending = (list: PendingScan[]) => {
  try { localStorage.setItem(PENDING_KEY, JSON.stringify(list)); } catch { /* 저장 못 해도 순찰은 계속 */ }
};
const forgetPending = (scanId: string) => savePending(loadPending().filter((p) => p.scan_id !== scanId));

const loadSession = (): { session: string; staff_name: string; expires_at: string } | null => {
  try {
    const s = JSON.parse(localStorage.getItem(SESSION_KEY) || 'null');
    if (!s || new Date(s.expires_at).getTime() < Date.now() + 60_000) return null;
    return s;
  } catch { return null; }
};

const fmtTime = (iso: string) =>
  new Date(iso).toLocaleTimeString('ko-KR', { timeZone: 'Asia/Seoul', hour: '2-digit', minute: '2-digit' });

const wrap: React.CSSProperties = {
  minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center',
  justifyContent: 'center', background: '#f8fafc', padding: 24, textAlign: 'center', fontFamily: 'sans-serif',
};
const bigBtn: React.CSSProperties = {
  marginTop: 24, padding: '18px 36px', fontSize: 20, fontWeight: 700, color: '#fff',
  background: '#2563eb', border: 'none', borderRadius: 14, cursor: 'pointer', width: '100%', maxWidth: 360,
};

export default function PatrolScanPage() {
  const deviceToken = localStorage.getItem(PATROL_DEVICE_KEY);
  return deviceToken ? <DeviceFlow deviceToken={deviceToken} /> : <AccountFlow />;
}

/* ───────────── ① 순찰 태블릿 ───────────── */
function DeviceFlow({ deviceToken }: { deviceToken: string }) {
  const { token } = useParams<{ token: string }>();
  const [stage, setStage] = useState<'loading' | 'who' | 'scanning' | 'check' | 'saving' | 'done' | 'error'>('loading');
  const [message, setMessage] = useState('');
  const [people, setPeople] = useState<Person[]>([]);
  const [storeName, setStoreName] = useState('');
  const [picked, setPicked] = useState<Person | null>(null);
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState('');
  const [result, setResult] = useState<ScanResult | null>(null);
  const [marks, setMarks] = useState<Record<number, Mark[]>>({});
  const [openSeat, setOpenSeat] = useState<number | null>(null);
  const [memos, setMemos] = useState<Record<number, string>>({});   // 학생별 한 줄 메모
  const [zoneNote, setZoneNote] = useState('');                      // 구역 특이사항
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  // 2026-10-06 대표 지시 — 점검 완료 직후 잘못 누른 표시를 바로 취소
  type SavedItem = { finding_id: string; seat_number: number; student_name: string | null; item_name: string; emoji: string; points: number; point_type: string };
  const [savedItems, setSavedItems] = useState<SavedItem[]>([]);
  const [cancelled, setCancelled] = useState<Record<string, boolean>>({});
  const [cancelBusy, setCancelBusy] = useState<string | null>(null);
  const [cancelMinutes, setCancelMinutes] = useState(30);
  // ⭐ 2026-10-16 우장산 — 저장 실패를 alert 로만 알리면, alert 가 막히는 기기에서는 아무 표시 없이
  //   점검표로 되돌아가 순찰자가 '끝난 줄' 안다(그날 QR 만 찍히고 완료가 없는 칸 2건). 화면 안에 남긴다.
  const [saveFailMsg, setSaveFailMsg] = useState('');
  const [cancelFailMsg, setCancelFailMsg] = useState('');
  // 앞 구역 미완료 — 상태: 대기 / 저장 중 / 저장됨 / 실패 문구
  const [prevPending, setPrevPending] = useState<PendingScan[]>([]);
  const [prevState, setPrevState] = useState<Record<string, string>>({});

  // 🔴 시간 제한 없이 fetch 하면 연결이 끊긴 순간 '저장 중…'에서 끝없이 멈춘다 → REQUEST_TIMEOUT_MS 뒤 실패로 본다
  const call = useCallback((path: string, init: RequestInit = {}) => {
    const headers = new Headers(init.headers || {});
    headers.set('X-Patrol-Device', deviceToken);
    const s = loadSession();
    if (s) headers.set('X-Patrol-Session', s.session);
    if (init.body) headers.set('Content-Type', 'application/json');
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), REQUEST_TIMEOUT_MS);
    return fetch(`${API_BASE_URL}${path}`, { ...init, headers, signal: ctrl.signal })
      .finally(() => clearTimeout(timer));
  }, [deviceToken]);

  const fail = (m: string) => { setMessage(m); setStage('error'); };

  // 학생별 오늘 순찰 기록 (2026-10-02) — 자리를 누르면 그 학생이 오늘 순찰 때마다 어땠는지 보인다
  const [history, setHistory] = useState<Record<string, { checks: { time: string; status: string; label: string; items?: string | null }[] } | null>>({});
  useEffect(() => {
    if (openSeat == null || !result) return;
    const sid = result.seats.find((x) => x.seat_number === openSeat)?.student_id;
    if (!sid || history[sid] !== undefined) return;
    setHistory((h) => ({ ...h, [sid]: null }));
    call(`/api/v1/patrol-device/students/${sid}/checks`)
      .then((r) => (r.ok ? r.json() : { checks: [] }))
      .then((d) => setHistory((h) => ({ ...h, [sid]: { checks: d.checks || [] } })))
      .catch(() => setHistory((h) => ({ ...h, [sid]: { checks: [] } })));
  }, [openSeat, result, call, history]);

  const scan = useCallback(async () => {
    setStage('scanning');
    try {
      const r = await call(`/api/v1/patrol-device/scan/${token}`, { method: 'POST' });
      const d = await r.json().catch(() => ({} as any));
      if (r.status === 403 && d.detail === '순찰자 확인이 필요합니다') {
        localStorage.removeItem(SESSION_KEY);
        setStage('who');
        return;
      }
      if (!r.ok) { fail(typeof d.detail === 'string' ? d.detail : '기록에 실패했습니다.'); return; }
      setResult(d);
      setSaveFailMsg('');
      // 같은 구역을 몇 분 안에 다시 찍은 건 한 방문(순찰표도 묶는다) → 앞 기록은 알리지 않고 잊는다
      const nowAt = new Date(d.scanned_at).getTime();
      const kept = loadPending().filter((p) => p.scan_id !== d.scan_id
        && !(p.zone_name === d.zone_name && nowAt - new Date(p.scanned_at).getTime() <= DUP_SCAN_MS));
      // 🔴 찍은 본인 것만 보인다 — 학생별 순찰 기록은 QR 찍은 사람 이름으로 남아서, 다른 사람이 누르면
      //    보지도 않은 '이상 없음'이 앞 사람 이름으로 기록된다. (본인이 다시 오면 그때 보이도록 남겨 둔다)
      const mine = kept.filter((p) => !p.staff_name || p.staff_name === d.staff_name);
      // 30분 지난 건 완료할 수 없으니 이번에 한 번만 알리고 잊는다(매번 다시 뜨지 않게)
      const expiredIds = new Set(mine.filter((p) => nowAt - new Date(p.scanned_at).getTime() > COMPLETE_LIMIT_MS).map((p) => p.scan_id));
      savePending([...kept.filter((p) => !expiredIds.has(p.scan_id)),
        { scan_id: d.scan_id, zone_name: d.zone_name, scanned_at: d.scanned_at, staff_name: d.staff_name }]);
      setPrevPending(mine);
      setPrevState({});
      setMarks({});
      setMemos({});
      setZoneNote('');
      setStage('check');
    } catch { fail('연결에 실패했습니다. 인터넷 상태를 확인해 주세요.'); }
  }, [call, token]);

  useEffect(() => {
    (async () => {
      try {
        const r = await call('/api/v1/patrol-device/me');
        const d = await r.json().catch(() => ({} as any));
        if (!r.ok) { fail(typeof d.detail === 'string' ? d.detail : '순찰 기기 확인에 실패했습니다.'); return; }
        setPeople(d.people || []);
        setStoreName(d.store_name || '');
        if (loadSession()) scan(); else setStage('who');
      } catch { fail('연결에 실패했습니다. 인터넷 상태를 확인해 주세요.'); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (fullPin: string) => {
    if (!picked) return;
    setPinError('');
    try {
      const r = await call('/api/v1/patrol-device/login', {
        method: 'POST', body: JSON.stringify({ user_id: picked.id, pin: fullPin }),
      });
      const d = await r.json().catch(() => ({} as any));
      if (!r.ok) { setPin(''); setPinError(typeof d.detail === 'string' ? d.detail : '확인에 실패했습니다.'); return; }
      localStorage.setItem(SESSION_KEY, JSON.stringify({ session: d.session, staff_name: d.staff_name, expires_at: d.expires_at }));
      setPicked(null); setPin('');
      // 점검 도중 '바꾸기'로 온 경우엔 같은 점검표로 돌아간다 — 다시 찍으면 기록이 하나 더 생긴다
      if (result) setStage('check'); else scan();
    } catch { setPinError('연결에 실패했습니다.'); }
  };

  const pressKey = (k: string) => {
    if (k === '⌫') { setPin(pin.slice(0, -1)); return; }
    const next = (pin + k).slice(0, 4);
    setPin(next);
    if (next.length === 4) login(next);
  };

  const toggle = (seat: number, m: Mark) => {
    const cur = marks[seat] || [];
    const same = (x: Mark) => x.item_id === m.item_id;
    const next = cur.some(same) ? cur.filter((x) => !same(x)) : [...cur, m];
    setMarks({ ...marks, [seat]: next });
  };

  const complete = async () => {
    if (!result) return;
    setSaveFailMsg('');
    setStage('saving');
    const findings = Object.entries(marks).flatMap(([seat, ms]) =>
      ms.map((m) => ({ seat_number: Number(seat), kind: 'item', item_id: m.item_id, memo: memos[Number(seat)] || null })));
    try {
      const r = await call(`/api/v1/patrol-device/scans/${result.scan_id}/complete`, {
        method: 'POST', body: JSON.stringify({ findings, note: zoneNote.trim() || null }),
      });
      const d = await r.json().catch(() => ({} as any));
      if (r.status === 403 && d.detail === '순찰자 확인이 필요합니다') {
        localStorage.removeItem(SESSION_KEY);
        fail('순찰자 확인 시간이 지났습니다. QR 을 다시 찍고 번호를 넣어 주세요.');
        return;
      }
      // 409 = 앞서 누른 요청이 실제로는 저장됐다(응답만 늦게 와서 시간 제한에 걸린 경우 등) → 완료로 본다
      if (r.status === 409) {
        forgetPending(result.scan_id);
        setSaveErrors([]); setSavedItems([]); setCancelled({});
        setStage('done');
        return;
      }
      if (!r.ok) {
        setStage('check');
        // 서비스 워커는 연결이 끊기면 예외 대신 빈 503 을 돌려준다 → 연결 문제로 안내
        setSaveFailMsg(typeof d.detail === 'string'
          ? `점검 완료가 저장되지 않았습니다 — ${d.detail}`
          : r.status === 503
            ? '인터넷 연결이 끊겨 점검 완료가 저장되지 않았습니다. 연결을 확인하고 아래 버튼을 다시 눌러 주세요.'
            : `점검 완료가 저장되지 않았습니다(오류 ${r.status}). 아래 버튼을 다시 눌러 주세요.`);
        return;
      }
      forgetPending(result.scan_id);
      setSaveErrors(d.errors || []);
      setSavedItems(d.items || []);
      setCancelled({});
      if (d.cancel_minutes) setCancelMinutes(d.cancel_minutes);
      setStage('done');
    } catch (e: any) {
      setStage('check');
      setSaveFailMsg(e?.name === 'AbortError'
        ? '서버 응답이 없어 점검 완료가 저장되지 않았습니다. 인터넷 연결을 확인하고 아래 버튼을 다시 눌러 주세요.'
        : '인터넷 연결이 끊겨 점검 완료가 저장되지 않았습니다. 연결을 확인하고 아래 버튼을 다시 눌러 주세요.');
    }
  };

  // 앞 구역을 '이상 없음'으로 바로 완료 — 표시할 학생이 있었으면 원장님께 알리도록 안내한다
  const completePrev = async (p: PendingScan) => {
    setPrevState((m) => ({ ...m, [p.scan_id]: 'saving' }));
    try {
      const r = await call(`/api/v1/patrol-device/scans/${p.scan_id}/complete`, {
        method: 'POST', body: JSON.stringify({ findings: [], note: null }),
      });
      const d = await r.json().catch(() => ({} as any));
      if (r.ok || r.status === 409) {
        forgetPending(p.scan_id);
        setPrevState((m) => ({ ...m, [p.scan_id]: 'done' }));
        return;
      }
      if (r.status === 400 || r.status === 404) forgetPending(p.scan_id);   // 30분 지남·없는 기록 — 더 할 수 없다
      setPrevState((m) => ({ ...m, [p.scan_id]: typeof d.detail === 'string' ? d.detail
        : r.status === 503 ? '인터넷 연결이 끊겨 저장하지 못했습니다. 다시 눌러 주세요.' : `저장하지 못했습니다(오류 ${r.status}).` }));
    } catch {
      setPrevState((m) => ({ ...m, [p.scan_id]: '연결에 실패해 저장하지 못했습니다. 다시 눌러 주세요.' }));
    }
  };
  const dismissPrev = (p: PendingScan) => {
    forgetPending(p.scan_id);
    setPrevPending((list) => list.filter((x) => x.scan_id !== p.scan_id));
  };

  const cancelItem = async (it: SavedItem) => {
    setCancelBusy(it.finding_id);
    setCancelFailMsg('');
    try {
      const r = await call(`/api/v1/patrol-device/findings/${it.finding_id}/cancel`, { method: 'POST' });
      const d = await r.json().catch(() => ({} as any));
      if (r.ok) setCancelled((c) => ({ ...c, [it.finding_id]: true }));
      else setCancelFailMsg(`${it.seat_number}번 취소 실패 — ${typeof d.detail === 'string' ? d.detail : '취소하지 못했습니다.'}`);
    } catch { setCancelFailMsg(`${it.seat_number}번 취소 실패 — 연결에 실패했습니다. 다시 눌러 주세요.`); }
    finally { setCancelBusy(null); }
  };

  const switchPerson = () => { localStorage.removeItem(SESSION_KEY); setStage('who'); };
  const session = loadSession();

  if (stage === 'loading' || stage === 'scanning') return <div style={wrap}><div style={{ color: '#6b7280' }}>확인 중…</div></div>;
  if (stage === 'error') {
    return (
      <div style={wrap}>
        <div style={{ fontSize: 48 }}>⚠️</div>
        <div style={{ color: '#374151', marginTop: 12, fontSize: 17, lineHeight: 1.6 }}>{message}</div>
      </div>
    );
  }

  if (stage === 'who') {
    return (
      <div style={{ ...wrap, justifyContent: 'flex-start', paddingTop: 48 }}>
        <div style={{ fontSize: 15, color: '#6b7280' }}>{storeName} 순찰</div>
        <h1 style={{ fontSize: 26, margin: '6px 0 20px' }}>{picked ? `${picked.name} 님, 번호 4자리` : '누가 순찰하나요?'}</h1>
        {!picked ? (
          people.length === 0 ? (
            <div style={{ color: '#6b7280', lineHeight: 1.7 }}>
              번호가 등록된 순찰자가 없습니다.<br />원장님이 [순찰 QR] 화면의 「순찰자 번호」에서 먼저 지정해 주세요.
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 12, width: '100%', maxWidth: 560 }}>
              {people.map((p) => (
                <button key={p.id} disabled={p.locked} onClick={() => { setPicked(p); setPin(''); setPinError(''); }}
                  style={{ padding: '18px 8px', fontSize: 18, fontWeight: 700, borderRadius: 12, cursor: p.locked ? 'not-allowed' : 'pointer',
                    border: '1px solid #d1d5db', background: p.locked ? '#f3f4f6' : '#fff', color: p.locked ? '#9ca3af' : '#111827' }}>
                  {p.name}{p.locked && <div style={{ fontSize: 12, fontWeight: 500 }}>잠김 · 원장님께 요청</div>}
                </button>
              ))}
            </div>
          )
        ) : (
          <>
            <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
              {[0, 1, 2, 3].map((i) => (
                <div key={i} style={{ width: 44, height: 52, borderRadius: 10, border: '2px solid #d1d5db', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', fontSize: 28, background: '#fff' }}>{pin[i] ? '●' : ''}</div>
              ))}
            </div>
            {pinError && <div style={{ color: '#dc2626', marginBottom: 8 }}>{pinError}</div>}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 88px)', gap: 10 }}>
              {['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', '⌫'].map((k, i) => (
                k === '' ? <span key={i} /> : (
                  <button key={i} onClick={() => pressKey(k)}
                    style={{ height: 64, fontSize: 26, fontWeight: 700, borderRadius: 12, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer' }}>{k}</button>
                )
              ))}
            </div>
            <button onClick={() => { setPicked(null); setPin(''); setPinError(''); }}
              style={{ marginTop: 18, background: 'none', border: 'none', color: '#2563eb', fontSize: 16, cursor: 'pointer' }}>← 다른 사람 고르기</button>
          </>
        )}
      </div>
    );
  }

  if (!result) return null;

  if (stage === 'done') {
    const n = Object.values(marks).reduce((a, m) => a + m.length, 0) - Object.keys(cancelled).length;
    return (
      <div style={wrap}>
        <div style={{ fontSize: 64 }}>✅</div>
        <h1 style={{ fontSize: 26, margin: '8px 0 0', color: '#16a34a' }}>{result.zone_name} 점검 완료</h1>
        <div style={{ color: '#6b7280', marginTop: 6 }}>{fmtTime(result.scanned_at)} · {result.staff_name} · {n === 0 ? '이상 없음' : `${n}건 표시`}</div>
        {saveErrors.length > 0 && (
          <div style={{ marginTop: 12, color: '#b91c1c', fontSize: 14, lineHeight: 1.6 }}>
            저장하지 못한 것:<br />{saveErrors.map((e) => <div key={e}>{e}</div>)}
          </div>
        )}
        {savedItems.length > 0 && (
          <div style={{ marginTop: 16, width: '100%', maxWidth: 480, textAlign: 'left' }}>
            <div style={{ fontSize: 14, color: '#6b7280', marginBottom: 6 }}>
              방금 표시한 것 — 잘못 눌렀으면 {cancelMinutes}분 안에 여기서 바로 취소할 수 있습니다
            </div>
            {cancelFailMsg && (
              <div role="alert" style={{ marginBottom: 8, padding: '10px 12px', borderRadius: 10, background: '#fef2f2',
                border: '1px solid #fca5a5', color: '#b91c1c', fontSize: 15, fontWeight: 600 }}>{cancelFailMsg}</div>
            )}
            {savedItems.map((it) => {
              const done = cancelled[it.finding_id];
              const pts = it.points > 0 ? ` · ${it.point_type === 'merit' ? '상점' : '벌점'} ${it.points}점` : '';
              return (
                <div key={it.finding_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  gap: 8, padding: '10px 12px', marginBottom: 6, borderRadius: 10, background: done ? '#f3f4f6' : '#fff',
                  border: '1px solid #e5e7eb', fontSize: 16 }}>
                  <span style={{ color: done ? '#9ca3af' : '#111827', textDecoration: done ? 'line-through' : 'none' }}>
                    {it.seat_number}번 {it.student_name || ''} · {it.emoji} {it.item_name}{pts}
                  </span>
                  {done ? (
                    <span style={{ color: '#6b7280', fontSize: 14 }}>취소됨</span>
                  ) : (
                    <button onClick={() => cancelItem(it)} disabled={cancelBusy === it.finding_id}
                      style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #dc2626', background: '#fff',
                        color: '#dc2626', fontSize: 15, fontWeight: 600 }}>
                      {cancelBusy === it.finding_id ? '취소 중…' : '취소'}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {result.round && (
          <div style={{ marginTop: 16, fontSize: 16, lineHeight: 1.7 }}>
            {result.round_left.length === 0
              ? <b style={{ color: '#166534' }}>{result.round} 순찰 — 모든 구역을 돌았습니다</b>
              : <>{result.round} 순찰 남은 구역: <b>{result.round_left.join(', ')}</b></>}
          </div>
        )}
        <div style={{ color: '#9ca3af', marginTop: 20, fontSize: 14 }}>다음 구역 QR 을 찍어 주세요.</div>
      </div>
    );
  }

  // 점검표 — 배치도 그대로 (2026-10-02 대표 "레이아웃 고정, 학생을 누르면 볼 수 있게, 최대한 한눈에")
  const total = Object.values(marks).reduce((a, m) => a + m.length, 0);
  const rooms: { key: string; name: string; seats: Seat[] }[] = [];
  for (const s of result.seats) {
    const key = s.room_id || '_';
    let g = rooms.find((x) => x.key === key);
    if (!g) { g = { key, name: s.room_name || '', seats: [] }; rooms.push(g); }
    g.seats.push(s);
  }
  const sel = openSeat != null ? result.seats.find((s) => s.seat_number === openSeat) : null;
  const seatColor = (s: Seat) => {
    if (!s.student_id) return { bg: '#f3f4f6', fg: '#9ca3af', bd: '#e5e7eb' };
    if (s.state === 'present') return { bg: '#dcfce7', fg: '#14532d', bd: '#86efac' };
    if (s.state === 'outing') return { bg: '#fef3c7', fg: '#78350f', bd: '#fcd34d' };
    return { bg: '#fff', fg: '#6b7280', bd: '#d1d5db' };
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', fontFamily: 'sans-serif', paddingBottom: saveFailMsg ? 280 : 150 }}>
      <div style={{ background: '#fff', borderBottom: '1px solid #e5e7eb', padding: '12px 16px', position: 'sticky', top: 0, zIndex: 2 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <h1 style={{ fontSize: 22, margin: 0 }}>{result.zone_name}</h1>
          <span style={{
            padding: '3px 10px', borderRadius: 8, fontSize: 13, fontWeight: 700,
            background: result.round ? (result.round_status === 'late' ? '#fef3c7' : '#dcfce7') : '#f3f4f6',
            color: result.round ? (result.round_status === 'late' ? '#92400e' : '#166534') : '#4b5563',
          }}>
            {result.round ? (result.round_status === 'late' ? `${result.round} · 늦음` : result.round) : '계획 외 순찰'}
          </span>
          <span style={{ marginLeft: 'auto', fontSize: 14, color: '#6b7280' }}>
            {session?.staff_name} <button onClick={switchPerson} style={{ border: 'none', background: 'none', color: '#2563eb', cursor: 'pointer' }}>바꾸기</button>
          </span>
        </div>
        <div style={{ display: 'flex', gap: 12, fontSize: 12, color: '#6b7280', marginTop: 6, flexWrap: 'wrap' }}>
          <span><i style={{ display: 'inline-block', width: 10, height: 10, background: '#dcfce7', border: '1px solid #86efac', marginRight: 4 }} />입실 중</span>
          <span><i style={{ display: 'inline-block', width: 10, height: 10, background: '#fef3c7', border: '1px solid #fcd34d', marginRight: 4 }} />외출 중</span>
          <span><i style={{ display: 'inline-block', width: 10, height: 10, background: '#fff', border: '1px solid #d1d5db', marginRight: 4 }} />안 옴·퇴실</span>
          <span><i style={{ display: 'inline-block', width: 10, height: 10, background: '#f3f4f6', border: '1px solid #e5e7eb', marginRight: 4 }} />빈 좌석</span>
          <span style={{ marginLeft: 'auto' }}>자리를 누르면 순찰 항목·메모</span>
        </div>
      </div>

      <div style={{ padding: 12, maxWidth: 980, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 14 }}>
        {prevPending.length > 0 && (
          <div role="alert" style={{ background: '#fff7ed', border: '2px solid #ea580c', borderRadius: 12, padding: 12 }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#9a3412' }}>⚠️ 앞 구역 점검 완료를 누르지 않았습니다</div>
            <div style={{ fontSize: 13, color: '#7c2d12', margin: '4px 0 8px', lineHeight: 1.5 }}>
              그대로 두면 순찰표에 '점검 안 함'으로 남습니다. 벌점·메모를 남길 학생이 있었다면 원장님께 알려 주세요.
            </div>
            {prevPending.map((p) => {
              const st = prevState[p.scan_id];
              const expired = Date.now() - new Date(p.scanned_at).getTime() > COMPLETE_LIMIT_MS;
              return (
                <div key={p.scan_id} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                  padding: '8px 0', borderTop: '1px solid #fed7aa' }}>
                  <span style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>{p.zone_name} · {fmtTime(p.scanned_at)} QR</span>
                  <span style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
                    {st === 'done' ? <span style={{ color: '#166534', fontWeight: 700 }}>✅ 점검 완료 저장됨</span>
                      : expired ? (
                        <>
                          <span style={{ fontSize: 13, color: '#9a3412' }}>30분이 지나 여기서 완료할 수 없습니다</span>
                          <button onClick={() => dismissPrev(p)} style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', fontSize: 13 }}>닫기</button>
                        </>
                      ) : (
                        <button onClick={() => completePrev(p)} disabled={st === 'saving'}
                          style={{ padding: '8px 12px', borderRadius: 8, border: 'none', background: '#16a34a', color: '#fff', fontSize: 14, fontWeight: 700 }}>
                          {st === 'saving' ? '저장 중…' : '이상 없음으로 점검 완료'}
                        </button>
                      )}
                  </span>
                  {st && st !== 'saving' && st !== 'done' && (
                    <div style={{ width: '100%', fontSize: 13, color: '#b91c1c', fontWeight: 600 }}>{st}</div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {result.seats.length === 0 && <div style={{ color: '#9ca3af', textAlign: 'center', padding: 24 }}>이 구역에 좌석이 없습니다.</div>}
        {rooms.map((g) => {
          const placed = g.seats.filter((s) => s.x != null && s.y != null);
          const minX = Math.min(...placed.map((s) => s.x as number));
          const minY = Math.min(...placed.map((s) => s.y as number));
          const maxX = Math.max(...placed.map((s) => (s.x as number) + s.w));
          const maxY = Math.max(...placed.map((s) => (s.y as number) + s.h));
          const W = Math.max(maxX - minX, 1), H = Math.max(maxY - minY, 1);
          return (
            <div key={g.key} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 12 }}>
              {g.name && <div style={{ fontSize: 14, fontWeight: 700, color: '#374151', marginBottom: 8 }}>{g.name}</div>}
              {placed.length > 0 && (
                <div style={{ position: 'relative', width: '100%', paddingTop: `${(H / W) * 100}%` }}>
                  {placed.map((s) => {
                    const c = seatColor(s);
                    const mine = marks[s.seat_number] || [];
                    const marked = mine.length > 0 || !!memos[s.seat_number];
                    return (
                      <button key={s.seat_number} disabled={!s.student_id} onClick={() => setOpenSeat(s.seat_number)}
                        style={{
                          position: 'absolute', left: `${(((s.x as number) - minX) / W) * 100}%`, top: `${(((s.y as number) - minY) / H) * 100}%`,
                          width: `${(s.w / W) * 100}%`, height: `${(s.h / H) * 100}%`, transform: s.rotation ? `rotate(${s.rotation}deg)` : undefined,
                          background: marked ? '#fde68a' : c.bg, color: c.fg, border: marked ? '2px solid #d97706' : `1px solid ${c.bd}`,
                          borderRadius: 6, padding: 0, cursor: s.student_id ? 'pointer' : 'default', overflow: 'hidden',
                          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', lineHeight: 1.1,
                        }}>
                        <span style={{ fontSize: 'clamp(9px, 1.6vw, 13px)', fontWeight: 800 }}>{s.seat_number}</span>
                        {s.student_name && <span style={{ fontSize: 'clamp(8px, 1.4vw, 12px)', whiteSpace: 'nowrap' }}>{s.student_name}</span>}
                        {mine.length > 0 && (
                          <span style={{ fontSize: 'clamp(9px, 1.5vw, 13px)' }}>
                            {mine.map((m) => result.items.find((i) => i.id === m.item_id)?.emoji || '•').join('')}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
              {/* 위치가 없는 좌석(배치도 미설정)은 아래 줄로 */}
              {g.seats.some((s) => s.x == null || s.y == null) && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                  {g.seats.filter((s) => s.x == null || s.y == null).map((s) => (
                    <button key={s.seat_number} disabled={!s.student_id} onClick={() => setOpenSeat(s.seat_number)}
                      style={{ ...(() => { const c = seatColor(s); return { background: c.bg, color: c.fg, border: `1px solid ${c.bd}` }; })(),
                        borderRadius: 6, padding: '6px 10px', fontSize: 13 }}>
                      {s.seat_number} {s.student_name || ''}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* 학생 자리를 누르면 — 아래에서 올라오는 칸 */}
      {sel && (
        <div onClick={() => setOpenSeat(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 5, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', width: '100%', maxWidth: 720, borderRadius: '16px 16px 0 0', padding: 18 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <span style={{ fontSize: 22, fontWeight: 800 }}>{sel.seat_number}</span>
              <span style={{ fontSize: 20, fontWeight: 700 }}>{sel.student_name}</span>
              {sel.state && STATE_LABEL[sel.state] && (
                <span style={{ background: STATE_LABEL[sel.state].bg, color: STATE_LABEL[sel.state].fg, borderRadius: 6, padding: '2px 8px', fontSize: 13, fontWeight: 600 }}>
                  {STATE_LABEL[sel.state].t}{sel.outing_type ? `(${sel.outing_type})` : ''}
                </span>
              )}
              <button onClick={() => setOpenSeat(null)} style={{ marginLeft: 'auto', border: 'none', background: 'none', fontSize: 22, cursor: 'pointer' }}>✕</button>
            </div>
            {sel.student_id && (() => {
              const h = history[sel.student_id];
              const icon: Record<string, string> = { studying: '😊', outing: '🚶', excused: '📄', away: '❌' };
              return (
                <div style={{ marginBottom: 12, padding: '8px 10px', background: '#f8fafc', borderRadius: 10, fontSize: 13 }}>
                  <div style={{ color: '#6b7280', marginBottom: 4 }}>오늘 순찰 기록</div>
                  {!h ? <span style={{ color: '#9ca3af' }}>불러오는 중…</span>
                    : h.checks.length === 0 ? <span style={{ color: '#9ca3af' }}>아직 없습니다 (이번 점검을 마치면 남습니다)</span>
                    : (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {h.checks.map((c, i) => (
                          <span key={i} title={c.label} style={{ whiteSpace: 'nowrap', color: c.status === 'away' ? '#dc2626' : '#374151' }}>
                            {icon[c.status] || '•'} {c.time}{c.items ? ` ${c.items}` : ''}
                          </span>
                        ))}
                      </div>
                    )}
                </div>
              );
            })()}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {result.items.map((it) => (
                <MarkBtn key={it.id} on={(marks[sel.seat_number] || []).some((m) => m.item_id === it.id)}
                  onClick={() => toggle(sel.seat_number, { kind: 'item', item_id: it.id })}>
                  {it.emoji} {it.name}{it.points > 0 ? ` (${it.point_type === 'merit' ? '상점' : '벌점'} ${it.points})` : ''}
                </MarkBtn>
              ))}
            </div>
            <input value={memos[sel.seat_number] || ''} maxLength={200} placeholder="메모 (선택) — 예: 2회째, 깨웠음"
              onChange={(e) => setMemos({ ...memos, [sel.seat_number]: e.target.value })}
              style={{ width: '100%', marginTop: 12, padding: '12px 14px', fontSize: 16, border: '1px solid #d1d5db', borderRadius: 10, boxSizing: 'border-box' }} />
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6 }}>메모는 항목을 하나 이상 골랐을 때 순찰노트·벌점 사유에 함께 남습니다.</div>
            <button onClick={() => setOpenSeat(null)} style={{ ...bigBtn, marginTop: 14, maxWidth: '100%' }}>확인</button>
          </div>
        </div>
      )}

      <div style={{ position: 'fixed', left: 0, right: 0, bottom: 0, background: '#fff', borderTop: '1px solid #e5e7eb', padding: 12, zIndex: 3 }}>
        <div style={{ maxWidth: 980, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {saveFailMsg && (
            <div role="alert" style={{ padding: '12px 14px', borderRadius: 10, background: '#fef2f2', border: '2px solid #dc2626',
              color: '#b91c1c', fontSize: 16, fontWeight: 700, lineHeight: 1.5 }}>⚠️ {saveFailMsg}</div>
          )}
          <input value={zoneNote} maxLength={300} placeholder="이 구역 특이사항 (선택) — 예: 에어컨 바람 강함, 7번 자리 의자 고장"
            onChange={(e) => setZoneNote(e.target.value)}
            style={{ padding: '12px 14px', fontSize: 16, border: '1px solid #d1d5db', borderRadius: 10 }} />
          <button onClick={complete} disabled={stage === 'saving'}
            style={{ ...bigBtn, marginTop: 0, maxWidth: '100%', background: total ? '#d97706' : '#16a34a' }}>
            {stage === 'saving' ? '저장 중…' : total ? `${total}건 표시하고 점검 완료` : '✅ 이상 없음 · 점검 완료'}
          </button>
        </div>
      </div>
    </div>
  );
}

function MarkBtn({ on, warn, onClick, children }: { on: boolean; warn?: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick} style={{
      padding: '10px 14px', fontSize: 15, borderRadius: 10, cursor: 'pointer', fontWeight: on ? 700 : 500,
      border: on ? '2px solid #d97706' : `1px solid ${warn ? '#fca5a5' : '#d1d5db'}`,
      background: on ? '#fef3c7' : '#fff', color: '#111827',
    }}>{on ? '✔ ' : ''}{children}</button>
  );
}

/* ───────────── ② 등록 안 된 기기 (종전 방식) ───────────── */
function AccountFlow() {
  const { token } = useParams<{ token: string }>();
  const [zoneName, setZoneName] = useState<string>('');
  const [seatCount, setSeatCount] = useState<number>(0);
  const [state, setState] = useState<'loading' | 'ready' | 'recording' | 'done' | 'need_login' | 'error'>('loading');
  const [message, setMessage] = useState<string>('');
  const [result, setResult] = useState<{ staff_name: string; scanned_at: string; round: string | null; round_status: string | null } | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/v1/patrol-qr/scan-info/${token}`);
        if (!res.ok) {
          setState('error');
          setMessage('유효하지 않은 QR 입니다. 재발급된 QR 일 수 있으니 원장님께 확인해 주세요.');
          return;
        }
        const data = await res.json();
        setZoneName(data.zone_name);
        setSeatCount(data.seat_count || 0);
        setState('ready');
      } catch {
        setState('error');
        setMessage('연결에 실패했습니다. 인터넷 상태를 확인해 주세요.');
      }
    })();
  }, [token]);

  const record = useCallback(async () => {
    setState('recording');
    try {
      // 1시간 뒤 토큰이 만료돼도 갱신해서 기록한다 (2026-09-24)
      const res = await authedFetch(`${API_BASE_URL}/api/v1/patrol-qr/scan/${token}`, { method: 'POST' });
      if (res.status === 401) { setState('need_login'); return; }
      if (!res.ok) {
        const d = await res.json().catch(() => ({} as any));
        // 로그인 정보가 아예 없으면 서버(HTTPBearer)가 403 "Not authenticated" 를 준다
        if (res.status === 403 && (!d.detail || d.detail === 'Not authenticated')) { setState('need_login'); return; }
        setState('error');
        setMessage(typeof d.detail === 'string' && d.detail !== 'Not authenticated' ? d.detail : '기록에 실패했습니다.');
        return;
      }
      const d = await res.json();
      setResult({ staff_name: d.staff_name, scanned_at: d.scanned_at, round: d.round || null, round_status: d.round_status || null });
      setState('done');
    } catch {
      setState('error');
      setMessage('연결에 실패했습니다. 인터넷 상태를 확인해 주세요.');
    }
  }, [token]);

  return (
    <div style={wrap}>
      {state === 'loading' && <div style={{ color: '#6b7280' }}>확인 중…</div>}

      {(state === 'ready' || state === 'recording') && (
        <>
          <div style={{ fontSize: 15, color: '#6b7280' }}>순찰 확인</div>
          <h1 style={{ fontSize: 30, margin: '8px 0 0' }}>{zoneName}</h1>
          {seatCount > 0 && <div style={{ color: '#6b7280', marginTop: 4 }}>좌석 {seatCount}개 구역</div>}
          <button style={bigBtn} onClick={record} disabled={state === 'recording'}>
            {state === 'recording' ? '기록 중…' : '✅ 순찰 완료 기록하기'}
          </button>
          <div style={{ color: '#9ca3af', marginTop: 16, fontSize: 13 }}>순찰 태블릿으로 찍으면 좌석 점검표가 열립니다.</div>
        </>
      )}

      {state === 'done' && result && (
        <>
          <div style={{ fontSize: 64 }}>✅</div>
          <h1 style={{ fontSize: 26, margin: '8px 0 0', color: '#16a34a' }}>순찰 기록 완료</h1>
          <div style={{ fontSize: 18, marginTop: 12 }}>{zoneName}</div>
          <div style={{ color: '#6b7280', marginTop: 4 }}>{fmtTime(result.scanned_at)} · {result.staff_name}</div>
          <div style={{
            marginTop: 14, padding: '8px 14px', borderRadius: 10, fontSize: 16, fontWeight: 700,
            background: result.round ? (result.round_status === 'late' ? '#fef3c7' : '#dcfce7') : '#f3f4f6',
            color: result.round ? (result.round_status === 'late' ? '#92400e' : '#166534') : '#4b5563',
          }}>
            {result.round
              ? (result.round_status === 'late' ? `${result.round} 순찰 — 늦게 기록됨` : `${result.round} 순찰로 기록됨`)
              : '계획 외 순찰로 기록됨'}
          </div>
          <div style={{ color: '#9ca3af', marginTop: 20, fontSize: 14 }}>이 창은 닫으셔도 됩니다. 다음 구역으로 이동해 주세요.</div>
        </>
      )}

      {state === 'need_login' && (
        <>
          <div style={{ fontSize: 48 }}>🔒</div>
          <h1 style={{ fontSize: 22, margin: '8px 0 0' }}>순찰 기기로 등록되지 않은 기기입니다</h1>
          <div style={{ color: '#6b7280', marginTop: 8, lineHeight: 1.6 }}>
            순찰 태블릿으로 찍어 주세요.<br />
            (원장님은 매장 계정으로 로그인한 뒤 다시 찍으면 기록됩니다)
          </div>
          <a href="/owner/login" style={{ ...bigBtn, display: 'block', textDecoration: 'none', boxSizing: 'border-box' }}>원장 로그인</a>
        </>
      )}

      {state === 'error' && (
        <>
          <div style={{ fontSize: 48 }}>⚠️</div>
          <div style={{ color: '#374151', marginTop: 12, fontSize: 16 }}>{message}</div>
        </>
      )}
    </div>
  );
}
